import type { TaskSummary } from '@pm/schemas';
import {
  Button,
  Counter,
  EmptyState,
  ErrorState,
  LabelCaps,
  PageHeader,
  Segmented,
  SkeletonRows,
} from '@pm/ui';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Inbox,
  Lock,
  Plus,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { TaskCompactRow } from '@/features/tasks/task-bits';
import { TaskCreateDialog } from '@/features/tasks/TaskCreateDialog';
import { useAuth, useMe } from '@/lib/auth';
import { useMyWork } from '@/lib/queries';
import { useUpdateViewPrefs, useViewPrefs } from '@/lib/view-prefs';

/** FRONTEND_UX §3 — hoje, semana, atrasadas, atribuídas, aguardando aprovação, pessoais. */
export function MyWorkPage() {
  const me = useMe();
  const { can } = useAuth();
  const { data, isLoading, isError, error, refetch } = useMyWork();
  const [create, setCreate] = useState<null | 'CORPORATE' | 'PERSONAL'>(null);
  const { prefs } = useViewPrefs();
  const updatePrefs = useUpdateViewPrefs();
  const mode = prefs.my_work.mode;

  const sections: {
    key: keyof NonNullable<typeof data>['counts'];
    label: string;
    icon: LucideIcon;
    danger?: boolean;
    personal?: boolean;
  }[] = [
    { key: 'overdue', label: 'Atrasadas', icon: AlertTriangle, danger: true },
    { key: 'today', label: 'Hoje', icon: CalendarDays },
    { key: 'week', label: 'Esta semana', icon: CalendarDays },
    {
      key: 'pending_approval',
      label: can('tasks.approve') ? 'Aguardando minha aprovação' : 'Aguardando aprovação',
      icon: CheckCircle2,
    },
    { key: 'assigned', label: 'Atribuídas a mim', icon: UserCheck },
    { key: 'personal', label: 'Pessoais · privado · só você vê', icon: Lock, personal: true },
  ];

  // Simplificada: só os quadros com demandas (enquanto carrega, mostra todos em esqueleto).
  const visible =
    mode === 'compact' && data ? sections.filter((s) => data.counts[s.key] > 0) : sections;

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Meu Trabalho"
        subtitle={`Olá, ${me.user.name.split(' ')[0]}. Isto é o que precisa da sua atenção.`}
        actions={
          <>
            <Segmented
              ariaLabel="Visualização"
              value={mode}
              onChange={(v) => updatePrefs({ my_work: { mode: v } })}
              options={[
                { value: 'full', label: 'Completa' },
                { value: 'compact', label: 'Simplificada' },
              ]}
            />
            <Button variant="ghost" onClick={() => setCreate('PERSONAL')}>
              <Plus /> Tarefa pessoal
            </Button>
            {can('tasks.create') ? (
              <Button variant="primary" onClick={() => setCreate('CORPORATE')}>
                <Plus /> Nova demanda
              </Button>
            ) : null}
          </>
        }
      />

      {isError ? (
        <ErrorState message={error.message} onRetry={() => void refetch()} />
      ) : visible.length === 0 ? (
        <section className="rounded-md border border-border bg-surface-1">
          <EmptyState
            icon={Inbox}
            title="Tudo em dia"
            description="Nenhuma demanda precisando da sua atenção agora."
            action={
              <Button size="sm" onClick={() => updatePrefs({ my_work: { mode: 'full' } })}>
                Ver visualização completa
              </Button>
            }
          />
        </section>
      ) : (
        visible.map((s) => {
          const items: TaskSummary[] = data?.[s.key] ?? [];
          const count = data?.counts[s.key] ?? 0;
          return (
            <section key={s.key} className="rounded-md border border-border bg-surface-1">
              <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                {s.personal ? <Lock className="size-3.5 text-text-3" /> : null}
                <LabelCaps className={s.danger && count > 0 ? 'text-danger' : undefined}>
                  {s.label}
                </LabelCaps>
                <Counter tone={s.danger && count > 0 ? 'danger' : 'neutral'}>{count}</Counter>
                {count > items.length ? (
                  <span className="ml-auto font-mono text-xs text-text-3">
                    mostrando {items.length}
                  </span>
                ) : null}
              </header>
              {isLoading ? (
                <SkeletonRows rows={2} />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={s.key === 'overdue' ? Inbox : s.icon}
                  title="Nada por aqui"
                  className="py-5"
                />
              ) : (
                items.map((t) => <TaskCompactRow key={t.id} task={t} />)
              )}
            </section>
          );
        })
      )}

      <TaskCreateDialog
        open={create !== null}
        onOpenChange={(o) => !o && setCreate(null)}
        scope={create ?? 'CORPORATE'}
      />
    </div>
  );
}
